import { formatInputNumberFormatter, parseFlexibleNumber } from '../utils/numberInput';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Modal, Table, Space, Select, Checkbox, Row, Col, InputNumber, Tooltip, Divider, notification, Popconfirm, Tag, Typography, Upload, Image, Switch, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import { DishService } from '../services/dishService';
import { Dish } from '../types/dish';
import { EditOutlined, PlusOutlined, QuestionCircleOutlined, DeleteOutlined, SearchOutlined, UploadOutlined, FileExcelOutlined, CopyOutlined } from '@ant-design/icons';
import { GroupService } from '../services/groupService';
import typeService from '../services/typeService';
import { Type } from '../types/type';
import { Group } from '../types/group';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import { CityService } from '../services/cityService';
import { MeasureService } from '../services/measureService';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { parseExcelToDishes, downloadDishExcelTemplate } from '../utils/excelUtils';
import { isCukcukMenuFile } from '../utils/cukcukParser';
import viVN from 'antd/locale/vi_VN';

const { Option } = Select;
const { Search } = Input;

const dishService = new DishService();
const groupService = new GroupService();
const cityService = new CityService();
const measureService = new MeasureService();

// Inline styles
const modalStyle = {
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '20px',
  },
  form: {
    backgroundColor: '#f9f9f9',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
};

const DishManagement: React.FC = () => {
  const navigate = useNavigate();
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDish, setEditingDish] = useState<Dish | null>(null);
  const [groups, setGroups] = useState<any>([]);
  const [types, setTypes] = useState<Type[]>([]);
  const [newGroup, setNewGroup] = useState<any>('');
  const [newType, setNewType] = useState<string>('');
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [loadingType, setLoadingType] = useState(false);
  const [loadingDish, setLoadingDish] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState<string | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [cities, setCities] = useState<any[]>([]);
  const [newCity, setNewCity] = useState<string>('');
  const [loadingCity, setLoadingCity] = useState(false);
  const [measures, setMeasures] = useState<any[]>([]);
  const [newMeasure, setNewMeasure] = useState<string>('');
  const [loadingMeasure, setLoadingMeasure] = useState(false);
  const [quantityPriceConfigs, setQuantityPriceConfigs] = useState<{ quantity: number; price: number; }[]>([]);
  const [sourcePriceModalVisible, setSourcePriceModalVisible] = useState(false);
  const [sourceConfigs, setSourceConfigs] = useState<{ source: string; price: number; }[]>([]);
  const [newSourcePrice, setNewSourcePrice] = useState<{ source: string; price: number; }>({
    source: '',
    price: 0
  });
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedHours, setSelectedHours] = useState<string[]>([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isTourOpen, setIsTourOpen] = useState(false);
  const storage = getStorage();

  // Tour refs
  const titleRef = useRef(null);
  const searchRef = useRef(null);
  const filtersRef = useRef(null);
  const addButtonRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const tableRef = useRef(null);

  // Tour steps
  const tourSteps: TourProps['steps'] = [
    {
      title: 'Quản lý món ăn',
      description: 'Chào mừng bạn đến với trang Quản lý món ăn! Đây là nơi bạn có thể quản lý toàn bộ thực đơn của quán, bao gồm thêm mới, chỉnh sửa và cập nhật thông tin các món ăn.',
      target: () => titleRef.current,
    },
    {
      title: 'Tìm kiếm món ăn',
      description: 'Sử dụng thanh tìm kiếm này để tìm món ăn theo tên hoặc mã món nhanh chóng trong danh sách.',
      target: () => searchRef.current,
    },
    {
      title: 'Bộ lọc món ăn',
      description: 'Lọc danh sách món ăn theo nhóm món và loại món để dễ dàng quản lý và tìm kiếm các món ăn theo danh mục.',
      target: () => filtersRef.current,
    },
    {
      title: 'Thêm món ăn mới',
      description: 'Nhấn vào đây để thêm một món ăn mới vào thực đơn. Bạn sẽ cần nhập đầy đủ thông tin như tên món, giá, mô tả, nhóm món, v.v.',
      target: () => addButtonRef.current,
    },
    {
      title: 'Tải lên file Excel',
      description: 'Tính năng này cho phép bạn tải lên file Excel để cập nhật hoặc thêm mới nhiều món ăn cùng một lúc, giúp tiết kiệm thời gian khi có nhiều món cần thêm.',
      target: () => uploadButtonRef.current,
    },
    {
      title: 'Bảng danh sách món ăn',
      description: 'Bảng hiển thị tất cả các món ăn trong thực đơn với đầy đủ thông tin như mã món, tên, giá, nhóm món, loại món và các nút thao tác để chỉnh sửa hoặc xóa.',
      target: () => tableRef.current,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn đã sẵn sàng quản lý thực đơn món ăn một cách hiệu quả. Hãy bắt đầu thêm các món ăn ngon cho quán nhé!',
      target: () => titleRef.current,
    },
  ];

  const fetchDishes = async () => {
    setLoading(true);
    try {
      const fetchedDishes = await dishService.getAllDishes();
      setDishes(fetchedDishes);
    } catch (error) {
      console.error('Error fetching dishes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const groups = await groupService.getAllGroups();
      console.log('Fetched groups:', groups);
      setGroups(groups);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchTypes = async () => {
    try {
      const types = await typeService.getAllTypes();
      console.log('Fetched types:', types);
      setTypes(types);
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const fetchedCities = await cityService.getAllCities();
      setCities(fetchedCities);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchMeasures = async () => {
    try {
      const fetchedMeasures = await measureService.getActiveMeasures();
      setMeasures(fetchedMeasures);
    } catch (error) {
      console.error('Error fetching measures:', error);
    }
  };

  useEffect(() => {
    fetchDishes();
    fetchGroups();
    fetchTypes();
    fetchCities();
    fetchMeasures();
  }, []);

  useEffect(() => {
    if (editingDish?.quantityPriceConfigurations) {
      setQuantityPriceConfigs(editingDish.quantityPriceConfigurations);
    } else {
      setQuantityPriceConfigs([]);
    }
  }, [editingDish]);

  useEffect(() => {
    if (editingDish?.priceBySourceConfiguration) {
      setSourceConfigs(editingDish.priceBySourceConfiguration);
    } else {
      setSourceConfigs([]);
    }
  }, [editingDish]);

  useEffect(() => {
    if (editingDish?.salesTimeFrame) {
      setSelectedDays(editingDish.salesTimeFrame.days || []);
      setSelectedHours(editingDish.salesTimeFrame.hours || []);
    } else {
      setSelectedDays([]);
      setSelectedHours([]);
    }
  }, [editingDish]);

  const handleAddOrUpdateDish = async (values: any) => {
    try {
      setLoading(true);
      const dishData = {
        ...values,
        price: Number(values.price),
        vat: Number(values.vat),
        preparationTime: Number(values.preparationTime),
        displayOrder: Number(values.displayOrder) || 0,
        priceConfiguration: {
          allowPriceEdit: values.allowPriceEdit || false,
          requireQuantity: values.requireQuantity || false,
          allowRemoveWithoutPermission: values.allowRemoveWithoutPermission || false,
        },
        quantityPriceConfigurations: values.quantityPriceConfigurations || [],
        priceBySourceConfiguration: values.priceBySourceConfiguration || [],
      };

      // Remove undefined or empty string values
      Object.keys(dishData).forEach(key => {
        if (dishData[key] === undefined || dishData[key] === '') {
          delete dishData[key];
        }
      });

      if (editingDish) {
        await dishService.updateDish(editingDish.id!, dishData);
        notification.success({
          message: 'Thành công',
          description: 'Món ăn đã được cập nhật thành công!',
        });
      } else {
        await dishService.addDish(dishData);
        notification.success({
          message: 'Thành công',
          description: 'Món ăn mới đã được thêm thành công!',
        });
      }
      setModalVisible(false);
      form.resetFields();
      fetchDishes();
    } catch (error) {
      console.error('Error saving dish:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể lưu món ăn.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (dish: Dish) => {
    console.log('dish', dish);

    const dishCopy = { ...dish };

    // Tìm thông tin nhóm và loại từ danh sách
    const group = groups.find((g: Group) => g.code === dishCopy.group);
    const type = types.find((t: Type) => t.code === dishCopy.type);

    console.log('group', group);
    console.log('type', type);

    const formData: any = {
      ...dishCopy,
      group: dishCopy.group ? dishCopy.group : '',
      type: dishCopy.type ? dishCopy.type : '',
      isVirtualDish: !!dishCopy.isVirtualDish,
      isBuffetTicket: !!dishCopy.isBuffetTicket,
      serviceConfiguration: !!dishCopy.serviceConfiguration,
      isSideDishQuantityNotUpdated: !!dishCopy.isSideDishQuantityNotUpdated,
      priceConfiguration: Object.entries(dishCopy.priceConfiguration || {
        allowPriceEdit: false,
        requireQuantity: false,
        allowRemoveWithoutPermission: false
      })
        .filter(([_, value]) => value === true)
        .map(([key]) => key)
    };

    // Set group name if group is found
    if (group) {
      formData.groupName = group.name;
    }

    // Set type name if type is found
    if (type) {
      formData.typeName = type.name;
    }

    // Set service configuration values if they exist
    if (dishCopy.serviceConfiguration && dishCopy.quantityPriceConfigurations?.[0]) {
      formData.serviceQuantity = dishCopy.quantityPriceConfigurations[0].quantity;
      formData.servicePrice = dishCopy.quantityPriceConfigurations[0].price;
    }

    // Set source price configurations if they exist
    if (dishCopy.priceBySourceConfiguration) {
      setSourceConfigs(dishCopy.priceBySourceConfiguration);
    } else {
      setSourceConfigs([]);
    }

    // Set sales time frame if it exists
    if (dishCopy.salesTimeFrame) {
      setSelectedDays(dishCopy.salesTimeFrame.days || []);
      setSelectedHours(dishCopy.salesTimeFrame.hours || []);
    } else {
      setSelectedDays([]);
      setSelectedHours([]);
    }

    setImageUrl(dish.imageUrl || '');
    form.setFieldsValue(formData);
    setEditingDish(dish);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    setLoadingDelete(id);
    try {
      await dishService.deleteDish(id);
      notification.success({
        message: 'Thành công',
        description: 'Món ăn đã được xóa thành công!',
      });
    } catch (error) {
      console.error('Error deleting dish:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa món ăn.',
      });
    } finally {
      setLoadingDelete(null);
      fetchDishes();
    }
  };

  const handleDeleteAll = async () => {
    if (dishes.length === 0) {
      notification.info({
        message: 'Không có dữ liệu',
        description: 'Hiện không có món ăn nào để xóa.',
      });
      return;
    }

    const targets = dishes.filter((dish) => dish.id);
    if (targets.length === 0) {
      notification.info({
        message: 'Không có dữ liệu',
        description: 'Không tìm thấy món ăn hợp lệ để xóa.',
      });
      return;
    }

    setDeletingAll(true);
    try {
      const results = await Promise.allSettled(
        targets.map((dish) => dishService.deleteDish(dish.id!))
      );

      const deletedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - deletedCount;

      if (failedCount === 0) {
        notification.success({
          message: 'Thành công',
          description: `Đã xóa ${deletedCount} món ăn.`,
        });
      } else {
        notification.warning({
          message: 'Xóa chưa hoàn tất',
          description: `Đã xóa ${deletedCount} món ăn, thất bại ${failedCount} món ăn.`,
        });
      }
    } catch (error) {
      console.error('Error deleting all dishes:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa tất cả món ăn.',
      });
    } finally {
      setDeletingAll(false);
      fetchDishes();
    }
  };

  const handleAddNewGroup = async () => {
    if (!newGroup) return; // Prevent adding if input is empty

    setLoadingGroup(true); // Set loading to true
    try {
      await groupService.addGroup({
        name: newGroup,
        code: newGroup.toUpperCase().replace(/\s+/g, '_'),
        displayOrder: 0,
        isActive: true
      });
      fetchGroups(); // Reload the group list
      setNewGroup(''); // Clear the input
      notification.success({
        message: 'Thành công',
        description: 'Nhóm mới đã được thêm thành công!',
      });
    } catch (error) {
      console.error('Error adding group:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể thêm nhóm mới.',
      });
    } finally {
      setLoadingGroup(false); // Reset loading state
    }
  };

  const handleAddNewType = async () => {
    if (!newType) return; // Prevent adding if input is empty

    setLoadingType(true); // Set loading to true
    try {
      await typeService.addType({
        name: newType,
        code: newType.toUpperCase().replace(/\s+/g, '_'),
        isActive: true
      });
      fetchTypes(); // Reload the type list
      setNewType(''); // Clear the input
      notification.success({
        message: 'Thành công',
        description: 'Loại món mới đã được thêm thành công!',
      });
    } catch (error) {
      console.error('Error adding type:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể thêm loại món mới.',
      });
    } finally {
      setLoadingType(false); // Reset loading state
    }
  };

  const handleAddNewCity = async () => {
    if (!newCity) return; // Prevent adding if input is empty
    setLoadingCity(true); // Set loading to true
    try {
      await cityService.addCity({ name: newCity });
      fetchCities(); // Reload the city list
      setNewCity(''); // Clear the input
    } catch (error) {
      console.error('Error adding city:', error);
    } finally {
      setLoadingCity(false); // Reset loading state
    }
  };

  const handleAddNewMeasure = async () => {
    if (!newMeasure) return;
    setLoadingMeasure(true);
    try {
      await measureService.addMeasure({
        name: newMeasure,
        code: newMeasure.toUpperCase().replace(/\s+/g, '_'),
        isActive: true
      });
      fetchMeasures();
      setNewMeasure('');
      notification.success({
        message: 'Thành công',
        description: 'Đơn vị đo lường mới đã được thêm thành công!',
      });
    } catch (error) {
      console.error('Error adding measure:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể thêm đơn vị đo lường mới.',
      });
    } finally {
      setLoadingMeasure(false);
    }
  };

  const handleImportDefaultMeasures = async () => {
    const defaultMeasures = [
      "Ấm", "Âu", "BAG", "Bàn", "Bảng", "BÁNH", "Bao", "Bao 25", "Bắp", "Bát",
      "Bì/BÌ", "Bìa", "Bịch", "Bình", "Bó", "Bộ", "Bọc", "Bó Hoa", "Bom", "Bông",
      "Bottle", "Bowl", "Box", "Bức", "Ca", "Cái", "Can", "Cành", "Cặp", "Carafe",
      "Cây", "Chai", "Chảo", "Chậu", "Chén", "Chỉ", "Chiếc", "Chục", "CHUM", "Cốc",
      "Cọc", "Cocktail", "Combo", "Con", "Cốt", "Cục", "Cuốn", "Cuộn", "Cup", "Dàn",
      "Dẻ", "Đêm", "Đĩa", "Dịch Vụ", "Điếu", "Dish", "Đoàn", "Đôi", "Dram", "Each",
      "Giờ", "Giỏ Hàng", "Giọt", "GL", "Glass", "Gói", "Gói 1 Kg", "Gram", "GUEST", "HẠT",
      "Hộp", "Hộp 16", "Hộp 24", "Hũ", "JAR", "Jug", "Kệ", "Keg", "Keng", "Két",
      "Kg", "Kg (Cốt)", "Khách", "Khay", "Khúc", "Kiện", "Kwh", "Lá", "Lẵng", "Lạng",
      "Lít", "Lọ", "Loaf", "Lốc", "Lon", "Lồng", "Lượng", "Ly", "M2", "Mâm",
      "Mẫu", "Mẻ", "Mét", "Mẹt", "Mét Khối", "Miếng", "Miligam", "ML", "Món", "Mug",
      "MÚI", "Muỗng", "Nải", "Nậm", "Ngày", "Người", "NHÁNH", "Niêu", "Night", "Nồi",
      "Ổ", "Ống", "Pack", "Part", "Pax", "PCS", "Person", "Phần", "Phin", "Phòng",
      "Piece", "Plate", "Portion", "Pot", "Quả", "Que", "Quý", "Quyển", "Ram", "Rổ",
      "Roll", "Serve", "Serving", "Set", "Shot", "Show", "Số", "Sợi", "Suất", "Tá",
      "Tag", "Tấm", "Tập", "Tép", "Tháng", "Thanh", "Tháp", "Thẻ", "Thếp", "Thố",
      "Thùng", "Tiệc", "TIN", "Tô", "Trái", "Tray", "Túi", "Túi 500G", "Tuýp", "VÁ",
      "VẠI", "Vé", "Vỉ", "Viên", "Vnđ", "Vòng", "XÁCH", "Xấp", "Xị", "Xiên",
      "Xô", "Xửng", "Lượt", "Lần", "Phút"
    ];

    setLoadingMeasure(true);
    try {
      const addPromises = defaultMeasures.map(name =>
        measureService.addMeasure({
          name,
          code: name.toUpperCase().replace(/\s+/g, '_').replace(/[\/\\]/g, '_'),
          isActive: true
        })
      );

      await Promise.all(addPromises);

      notification.success({
        message: 'Thành công',
        description: 'Đã thêm danh sách đơn vị tính mặc định!',
      });

      fetchMeasures();
    } catch (error) {
      console.error('Error importing default measures:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể thêm danh sách đơn vị tính mặc định.',
      });
    } finally {
      setLoadingMeasure(false);
    }
  };

  const handleAddQuantityPrice = () => {
    const newConfig = {
      quantity: form.getFieldValue('newQuantity') || 0,
      price: form.getFieldValue('newPrice') || 0,
    };

    if (newConfig.quantity && newConfig.price) {
      const updatedConfigs = [...quantityPriceConfigs, newConfig];
      setQuantityPriceConfigs(updatedConfigs);
      form.setFieldValue('quantityPriceConfigurations', updatedConfigs);
      form.setFieldsValue({
        newQuantity: undefined,
        newPrice: undefined,
      });
    }
  };

  const handleRemoveQuantityPrice = (index: number) => {
    const updatedConfigs = quantityPriceConfigs.filter((_, i) => i !== index);
    setQuantityPriceConfigs(updatedConfigs);
    form.setFieldValue('quantityPriceConfigurations', updatedConfigs);
  };

  const handleAddSourcePrice = () => {
    if (newSourcePrice.source && newSourcePrice.price >= 0) {
      const updatedConfigs = [...sourceConfigs, newSourcePrice];
      setSourceConfigs(updatedConfigs);
      form.setFieldValue('priceBySourceConfiguration', updatedConfigs);
      setNewSourcePrice({ source: '', price: 0 });
      setSourcePriceModalVisible(false);
    }
  };

  const handleRemoveSourcePrice = (index: number) => {
    const updatedConfigs = sourceConfigs.filter((_, i) => i !== index);
    setSourceConfigs(updatedConfigs);
    form.setFieldValue('priceBySourceConfiguration', updatedConfigs);
  };

  const handleImageUpload = async (file: File) => {
    setImageLoading(true);
    try {
      const storageRef = ref(storage, `8am-dish-images/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setImageUrl(downloadUrl);
      form.setFieldsValue({ imageUrl: downloadUrl });
      notification.success({
        message: 'Thành công',
        description: 'Tải ảnh lên thành công!',
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải ảnh lên.',
      });
    } finally {
      setImageLoading(false);
    }
  };

  const handleDeleteImage = async (url: string) => {
    try {
      const imageRef = ref(storage, url);
      await deleteObject(imageRef);
      setImageUrl('');
      form.setFieldsValue({ imageUrl: '' });
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const filteredDishes = dishes.filter(dish => {
    const matchesSearch = !searchTerm ||
      dish.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dish.code.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesGroup = !selectedGroup || dish.group === selectedGroup;
    const matchesType = !selectedType || dish.type === selectedType;

    return matchesSearch && matchesGroup && matchesType;
  });

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1
    },
    {
      title: 'Mã món',
      dataIndex: 'code',
      key: 'code',
      width: 120
    },
    {
      title: 'Tên món',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: 'Giá',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (text: number) => text?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' đ'
    },
    {
      title: 'VAT (%)',
      dataIndex: 'vat',
      key: 'vat',
      width: 100,
      render: (vat: number) => `${vat}%`
    },
    {
      title: 'Nhóm món',
      dataIndex: 'group',
      key: 'group',
      width: 150,
      render: (groupId: string) => {
        const group = groups.find((g: Group) => g.id === groupId);
        return group ? group.name : groupId || '-';
      }
    },
    {
      title: 'Loại món',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (typeId: string) => {
        const type = types.find(t => t.id === typeId);
        return type ? type.name : typeId || '-';
      }
    },
    {
      title: 'Đơn vị tính',
      dataIndex: 'unit',
      key: 'unit',
      width: 150
    },
    {
      title: 'Trạng thái',
      key: 'isActive',
      dataIndex: 'isActive',
      width: 120,
      render: (isActive: boolean, record: Dish) => (
        <Switch
          checkedChildren="Hiển thị"
          unCheckedChildren="Ẩn"
          checked={isActive}
          onChange={(checked) => {
            if (record.id) {
              dishService.updateDish(record.id, { ...record, isActive: checked });
              fetchDishes();
            }
          }}
        />
      )
    },
    {
      title: 'Món ăn kèm',
      dataIndex: 'sideDish',
      key: 'sideDish',
      width: 120,
      render: (sideDish: string) => (
        sideDish === 'true' ? <div >Món ăn kèm</div> : <div>Món chính</div>
      )
    },
    {
      title: 'Thành phố',
      dataIndex: 'city',
      key: 'city',
      width: 120,
      render: (cityId: string) => {
        const city = cities.find(c => c.id === cityId);
        return city ? city.name : cityId || '-';
      }
    },
    {
      title: 'Cấu hình buffet',
      dataIndex: 'isBuffetTicket',
      key: 'isBuffetTicket',
      width: 150,
      render: (value: boolean) => (
        value ? <Tag color="blue">Có</Tag> : <Tag color="default">Không</Tag>
      )
    },
    {
      title: 'Customization',
      dataIndex: 'isVirtualDish',
      key: 'isVirtualDish',
      width: 150,
      render: (value: boolean) => (
        value ? <Tag color="blue">Có</Tag> : <Tag color="default">Không</Tag>
      )
    },
    {
      title: 'Sao chép tạo món mới',
      key: 'copy',
      width: 150,
      render: (_: any, record: Dish) => (
        <Button
          icon={<CopyOutlined />}
          onClick={() => {
            const newDish = { ...record };
            delete newDish.id;
            newDish.name = `${record.name} (Copy)`;
            newDish.code = `${record.code}_COPY`;
            setEditingDish(newDish);
            form.resetFields();
            form.setFieldsValue(newDish);
            setModalVisible(true);
          }}
        />
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: Dish) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Xóa món ăn"
            description="Bạn có chắc chắn muốn xóa món ăn này?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={loadingDelete === record.id}
              disabled={loadingDelete === record.id}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 ref={titleRef}>Quản lý món ăn</h1>
          <Button
            type="primary"
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsTourOpen(true)}
            style={{ marginLeft: 8 }}
          >
            Hướng dẫn
          </Button>
          {/* <Tag color="blue">{dishes.length} món ăn</Tag> */}
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters" ref={filtersRef}>
            <Space>
              <div ref={searchRef}>
                <Search
                  placeholder="Tìm kiếm theo tên hoặc mã món"
                  allowClear
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 300 }}
                />
              </div>
              <Select
                placeholder="Tất cả nhóm món"
                allowClear
                style={{ width: 200 }}
                onChange={(value) => setSelectedGroup(value)}
                options={groups.map((group: Group) => ({
                  label: group.code,
                  value: group.code
                }))}
              />
              <Select
                placeholder="Tất cả loại món"
                allowClear
                style={{ width: 200 }}
                onChange={(value) => setSelectedType(value)}
                options={types.map((type: Type) => ({
                  label: type.code,
                  value: type.code
                }))}
              />
            </Space>
          </div>

          <Space>
            <Popconfirm
              title="Xóa toàn bộ món ăn"
              description="Bạn có chắc chắn muốn xóa tất cả món ăn? Hành động này không thể hoàn tác."
              onConfirm={handleDeleteAll}
              okText="Xóa hết"
              cancelText="Hủy"
              okButtonProps={{ danger: true, loading: deletingAll }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deletingAll}
                disabled={deletingAll || dishes.length === 0}
              >
                Xóa tất cả
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingDish(null);
                form.resetFields();
                setModalVisible(true);
              }}
              className="admin-button"
              ref={addButtonRef}
            >
              Thêm món ăn mới
            </Button>

            <Tooltip title="Tải lên file Excel 8am hoặc export Thực đơn CUKCUK (.xls/.xlsx)">
              <Upload
                accept=".xlsx, .xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  setLoading(true);

                  const uploadFile = async () => {
                    if (await isCukcukMenuFile(file)) {
                      Modal.info({
                        title: 'File menu CUKCUK',
                        content:
                          'Import CUKCUK đã chuyển sang trang riêng để xem trước, kiểm tra mapping và tránh import trùng.',
                        okText: 'Mở Import CUKCUK',
                        onOk: () => navigate('/import-cukcuk'),
                      });
                      return;
                    }

                    const dishes = await parseExcelToDishes(file);
                    const result = await dishService.batchUpdateFromExcel(dishes);

                    notification.success({
                      message: 'Cập nhật thành công',
                      description: `Đã thêm ${result.added} món mới, cập nhật ${result.updated} món.${result.errors.length > 0 ? ` Có ${result.errors.length} lỗi.` : ''}`,
                    });

                    if (result.errors.length > 0) {
                      Modal.error({
                        title: 'Có lỗi xảy ra khi cập nhật',
                        content: (
                          <div>
                            <p>Các lỗi sau đã xảy ra:</p>
                            <ul>
                              {result.errors.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        ),
                        width: 600,
                      });
                    }
                  };

                  uploadFile()
                    .then(() => fetchDishes())
                    .catch((error) => {
                      notification.error({
                        message: 'Lỗi upload',
                        description: error?.message || 'Không thể đọc hoặc lưu file.',
                      });
                    })
                    .finally(() => setLoading(false));

                  return false;
                }}
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  className="admin-button"
                  ref={uploadButtonRef}
                >
                  Tải lên file Excel
                </Button>
              </Upload>
            </Tooltip>
          </Space>
        </div>
      </div>

      <div className="admin-form-section">
        <div className="admin-table-container">
          <Table
            className="admin-table"
            columns={columns}
            dataSource={filteredDishes}
            rowKey="id"
            loading={loading || loadingDelete !== null}
            scroll={{ x: 1500, y: 'calc(100vh - 280px)' }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Tổng số: ${total} món ăn`,
            }}
            ref={tableRef}
          />
        </div>
      </div>

      <Modal
        title={editingDish ? "Sửa thông tin món ăn" : "Thêm món ăn mới"}
        open={modalVisible}
        onCancel={() => {
          setEditingDish(null);
          setModalVisible(false);
        }}
        footer={null}
        width={800}
        className="admin-modal"
      >
        <div className="admin-form-container">
          <Form
            form={form}
            onFinish={handleAddOrUpdateDish}
            layout="vertical"
          >
            <Form.Item
              name="imageUrl"
              label={
                <div>
                  Hình ảnh món ăn
                  <Tooltip title="Tải lên hình ảnh món ăn (Kích thước khuyến nghị: 500x500px)">
                    <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                  </Tooltip>
                </div>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleImageUpload(file);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={imageLoading}>
                    Tải ảnh lên
                  </Button>
                </Upload>
                {imageUrl && (
                  <div style={{ position: 'relative', width: 'fit-content' }}>
                    <Image
                      src={imageUrl}
                      alt="Món ăn"
                      style={{ maxWidth: '200px', maxHeight: '200px' }}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteImage(imageUrl)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: 'rgba(255, 255, 255, 0.8)',
                      }}
                    />
                  </div>
                )}
              </div>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="id"
                  label={
                    <div>
                      ID
                      <Tooltip title="ID của món ăn, được tạo tự động">
                        <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                      </Tooltip>
                    </div>
                  }
                >
                  <Input placeholder='ID tự động' disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="code"
                  label={
                    <div>
                      Mã món
                      <Tooltip title="Nếu để trống, hệ thống sẽ tự động tạo một mã món">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                  rules={[{ max: 16, message: 'Mã món không được vượt quá 16 ký tự!' }]}
                >
                  <Input maxLength={16} placeholder='Nhập mã món (tối đa 16 ký tự)' />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="city" label="Thành phố" rules={[{ required: true, message: 'Vui lòng chọn thành phố!' }]}>
                  <Select
                    placeholder="Chọn thành phố"
                    options={cities.map((city) => ({ label: city.name, value: city.id }))}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const label = option?.label;
                      return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                    }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space style={{ padding: '0 8px 4px' }}>
                          <Input
                            placeholder="Nhập thành phố mới"
                            value={newCity}
                            onChange={(e) => setNewCity(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleAddNewCity();
                              }
                            }}
                          />
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={() => handleAddNewCity()}
                            disabled={loadingCity || !newCity} // Disable if loading or input is empty
                          >
                            Thêm
                          </Button>
                        </Space>
                      </>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="name" label="Tên" rules={[{ required: true, message: 'Vui lòng nhập tên món!' }]}>
                  <Input placeholder='Nhập tên món' />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="price" label="Giá" rules={[{ required: true, message: 'Vui lòng nhập giá!' }]}>
                  <InputNumber<number>
                    placeholder='Nhập giá'
                    style={{ width: '100%' }}
                    addonAfter='đ'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="isActive"
                  label={
                    <div>
                      Trạng thái hiển thị
                      <Tooltip title="Chọn trạng thái hiển thị của món ăn">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch checkedChildren="Hiển thị" unCheckedChildren="Ẩn món" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="barcode"
                  label={
                    <div>
                      Mã barcode
                      <Tooltip title="Nếu bạn sử dụng tính năng scan QR tại POS hay tạo mã barcode">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                >
                  <Input maxLength={15} placeholder='Nhập mã barcode (tối đa 15 ký tự)' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="sideDish"
                  label={
                    <div>
                      Món ăn kèm
                      <Tooltip title="Món ăn kèm không hiển thị trên menu chính">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="isSideDishQuantityNotUpdated"
                  label={
                    <div>
                      Không cập nhật số lượng món ăn kèm
                      <Tooltip title="Khi chọn, các món ăn kèm của món này sẽ không tính toán số lượng nhân với món chính!">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="unit" label="Đơn vị" rules={[{ required: true, message: 'Vui lòng chọn đơn vị!' }]}>
                  <Select
                    placeholder="Chọn đơn vị"
                    options={measures.map((measure) => ({ label: measure.name, value: measure.name }))}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const label = option?.label;
                      return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                    }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space style={{ padding: '0 8px 4px' }}>
                          <Input
                            placeholder="Nhập đơn vị mới"
                            value={newMeasure}
                            onChange={(e) => setNewMeasure(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleAddNewMeasure();
                              }
                            }}
                          />
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={() => handleAddNewMeasure()}
                            disabled={loadingMeasure || !newMeasure}
                          >
                            Thêm
                          </Button>
                        </Space>
                      </>
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="vat"
                  label={
                    <div>
                      VAT
                      <Tooltip title="Cấu hình VAT cho từng món chỉ hoạt động khi cấu hình nhà hàng VAT xuôi và hỗ trợ tính VAT trên từng món hoặc VAT ngược và hỗ trợ tính VAT trên từng món. Lưu ý nếu giá trị bằng 0 thì sẽ tự động nhận cấu hình VAT tại nhà hàng (trường hợp đặc biệt cấu hình VAT =-1, VAT món ăn sẽ nhận giá trị 0%, trường hợp cấu hình VAT =-2 là cấu hình món không chịu thuế, trường hợp cấu hình VAT =-3 là cấu hình món không đẩy sang bên hóa đơn điện tử)">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" placeholder="Nhập VAT" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="preparationTime"
                  label="Thời gian chế biến (phút)"
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="phút" placeholder="Nhập thời gian chế biến" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="displayOrder"
                  label={
                    <div>
                      Thứ tự hiển thị
                      <Tooltip title="Số thứ tự hiển thị của món ăn (số càng nhỏ hiển thị càng trước)">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                >
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="Nhập thứ tự hiển thị" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="isActive"
                  label={
                    <div>
                      Trạng thái hiển thị
                      <Tooltip title="Chọn trạng thái hiển thị của món ăn">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch checkedChildren="Hiển thị" unCheckedChildren="Ẩn món" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="priceConfiguration" label="Cấu hình sửa giá, nhập số lượng">
              <Checkbox.Group>
                <Checkbox value="allowPriceEdit">Cho phép sửa giá khi bán</Checkbox>
                <Checkbox value="requireQuantity">Yêu cầu nhập số lượng khi gọi đồ</Checkbox>
                <Checkbox value="allowRemoveWithoutPermission">Cho phép bỏ món mà không cần quyền áp dụng</Checkbox>
              </Checkbox.Group>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="isVirtualDish"
                  label={
                    <div>
                      Cấu hình món ảo
                      <Tooltip title="Món ảo là món có giá 0đ, và chỉ có tác dụng hỗ trợ thao tác gọi món khi món này có các món ăn kèm, món ảo sẽ không được lưu vào giỏ hàng">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="isBuffetTicket"
                  label={
                    <div>
                      Cấu hình món ăn là vé buffet
                      <Tooltip title="Vé buffet dùng để định nghĩa sẽ đi kèm với những món ăn nào, vì thế trong món ăn là vé buffet sẽ có thêm cấu hình danh sách món ăn KHÔNg đi kèm. Để dùng chức năng buffet bạn phải cấu hình nhà hàng là nhà hàng buffet.">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item key='group' name='group' label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Nhóm món</span>
                  </div>
                }
                >
                  <Select
                    placeholder="Chọn nhóm món"
                    options={groups.map((group: any) => ({
                      label: group.code,
                      value: group.id,
                      groupData: group // Lưu trữ toàn bộ thông tin nhóm
                    }))}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    onChange={(value, option: any) => {
                      // Khi chọn nhóm, tự động cập nhật tên nhóm
                      if (value && option?.groupData) {
                        form.setFieldsValue({
                          groupName: option.groupData.name
                        });
                      } else {
                        form.setFieldsValue({
                          groupName: ''
                        });
                      }
                    }}
                    filterOption={(input, option) => {
                      const label = option?.label;
                      return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                    }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space style={{ padding: '0 8px 4px' }}>
                          <Input
                            placeholder="Nhập nhóm món mới"
                            value={newGroup}
                            onChange={(e) => setNewGroup(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleAddNewGroup();
                              }
                            }}
                          />
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={handleAddNewGroup}
                            disabled={loadingGroup || !newGroup}
                          >
                            Thêm
                          </Button>
                        </Space>
                      </>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="groupName" label="Tên nhóm">
                  <Input placeholder='Nhập tên nhóm' disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="type" label="Loại món">
                  <Select
                    placeholder="Chọn loại món"
                    options={types.map((type) => ({
                      label: type.code,
                      value: type.id,
                      typeData: type // Lưu trữ toàn bộ thông tin loại món
                    }))}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    onChange={(value, option: any) => {
                      // Khi chọn loại món, tự động cập nhật tên loại
                      if (value && option?.typeData) {
                        form.setFieldsValue({
                          typeName: option.typeData.name
                        });
                      } else {
                        form.setFieldsValue({
                          typeName: ''
                        });
                      }
                    }}
                    filterOption={(input, option) => {
                      const label = option?.label;
                      return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                    }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space style={{ padding: '0 8px 4px' }}>
                          <Input
                            placeholder="Nhập loại món mới"
                            value={newType}
                            onChange={(e) => setNewType(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleAddNewType();
                              }
                            }}
                          />
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={handleAddNewType}
                            disabled={loadingType || !newType}
                          >
                            Thêm
                          </Button>
                        </Space>
                      </>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="typeName" label="Tên loại">
                  <Input placeholder='Nhập tên loại' disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="description" label="Mô tả">
                  <Input.TextArea />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="sku" label="SKU">
                  <Input placeholder='Nhập SKU' maxLength={50} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="vat"
                  label={
                    <div>
                      VAT (%)
                      <Tooltip title="Cấu hình VAT cho từng món">
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" placeholder="Nhập VAT" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="preparationTime"
                  label="Thời gian chế biến (phút)"
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="phút" placeholder="Nhập thời gian chế biến" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="priceConfiguration"
                  label="Cho phép sửa giá khi bán"
                  valuePropName="checked"
                >
                  <Checkbox value="allowPriceEdit">Cho phép sửa giá khi bán</Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="isVirtualDish"
                  label="Cấu hình món ảo"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="serviceConfiguration"
                  label="Cấu hình món dịch vụ"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="isBuffetTicket"
                  label="Cấu hình món ăn là vé buffet"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="displayOrder"
                  label="Thứ tự hiển thị"
                >
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="Nhập thứ tự hiển thị" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="imageUrl"
              label={
                <div>
                  Hình ảnh món ăn
                  <Tooltip title="Tải lên hình ảnh món ăn (Kích thước khuyến nghị: 500x500px)">
                    <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                  </Tooltip>
                </div>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleImageUpload(file);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={imageLoading}>
                    Tải ảnh lên
                  </Button>
                </Upload>
                {imageUrl && (
                  <div style={{ position: 'relative', width: 'fit-content' }}>
                    <Image
                      src={imageUrl}
                      alt="Món ăn"
                      style={{ maxWidth: '200px', maxHeight: '200px' }}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteImage(imageUrl)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: 'rgba(255, 255, 255, 0.8)',
                      }}
                    />
                  </div>
                )}
              </div>
            </Form.Item>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="qrCodeFormula"
                  label={
                    <div>
                      Công thức inQr cho máy pha trà
                      <Tooltip
                        title={
                          <div>
                            <div style={{ marginBottom: '8px' }}>Mã QR-code trên tem sẽ có định dạng:</div>
                            <div style={{ marginBottom: '8px' }}>Công thức + | tran_id | khoa đơn | id khác (nếu có)</div>
                            <div style={{ marginBottom: '8px' }}>
                              <div>Ví dụ công thức: M:250, A:30, C:50</div>
                              <div>Giải nghĩa:</div>
                              <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                                <li>Vỏ M: số lượng 250 ml</li>
                                <li>Vỏ A: số lượng 30 ml</li>
                                <li>Vỏ C: số lượng 50 ml</li>
                              </ul>
                            </div>
                            <div style={{ marginBottom: '8px' }}>Cấu hình QR vĩ dụ: M:250, A:30, C:50 | tran_id | khoa đơn | id khác (nếu có)</div>
                            <div style={{ fontStyle: 'italic' }}>Lưu ý:</div>
                            <ul style={{ marginLeft: '20px', marginTop: '4px', marginBottom: 0 }}>
                              <li>Món ăn kèm (topping) có khả năng công thức sẽ được tự động công thêm vào công thức của món chính.</li>
                            </ul>
                          </div>
                        }>
                        <span style={{ color: '#333', fontWeight: 'bold', cursor: 'pointer', marginLeft: '4px' }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Tooltip>
                    </div>
                  }
                >
                  <Input placeholder="Nhập công thức inQR" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.serviceConfiguration !== currentValues.serviceConfiguration
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('serviceConfiguration') ? (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 16 }}>
                      <Typography.Title level={5}>Cấu hình giá thay đổi theo số lượng</Typography.Title>
                    </div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="serviceQuantity"
                          label="Khai báo số lượng"
                          rules={[{ required: true, message: 'Vui lòng nhập số lượng!' }]}
                        >
                          <InputNumber style={{ width: '100%' }} min={1} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="servicePrice"
                          label="Khai báo giá khi vượt quá số lượng trên"
                          rules={[{ required: true, message: 'Vui lòng nhập giá!' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            // formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                            // parser={(value) => value ? Number(value.replace(/\./g, '')) : 0}
                            addonAfter="đ"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                ) : null
              }
            </Form.Item>
            <div style={{ marginBottom: 24 }}>
              <Typography.Title level={5}>Cấu hình giá theo nguồn</Typography.Title>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Button
                    type="dashed"
                    onClick={() => setSourcePriceModalVisible(true)}
                    icon={<PlusOutlined />}
                    style={{ marginBottom: 16 }}
                  >
                    Thêm nguồn
                  </Button>
                  {sourceConfigs.length > 0 && (
                    <Table
                      dataSource={sourceConfigs}
                      columns={[
                        {
                          title: 'Nguồn đơn',
                          dataIndex: 'source',
                          key: 'source'
                        },
                        {
                          title: 'Số tiền',
                          dataIndex: 'price',
                          key: 'price',
                          render: (price: number) => `${price.toLocaleString('vi-VN')} đ`
                        },
                        {
                          title: 'Thao tác',
                          key: 'action',
                          render: (_, __, index) => (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveSourcePrice(index)}
                            />
                          )
                        }
                      ]}
                      pagination={false}
                      size="small"
                    />
                  )}
                </Col>
              </Row>
            </div>
            <div style={{ marginBottom: 24 }}>
              <Typography.Title level={5}>Khung thời gian bán</Typography.Title>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text>Chọn ngày</Typography.Text>
                  <Tooltip title="Chọn các ngày trong tuần mà món ăn này được phép bán">
                    <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                  </Tooltip>
                </div>
                <div>
                  <Space wrap>
                    {[
                      { label: 'Thứ 2', value: 'MON' },
                      { label: 'Thứ 3', value: 'TUE' },
                      { label: 'Thứ 4', value: 'WED' },
                      { label: 'Thứ 5', value: 'THU' },
                      { label: 'Thứ 6', value: 'FRI' },
                      { label: 'Thứ 7', value: 'SAT' },
                      { label: 'Chủ nhật', value: 'SUN' }
                    ].map(day => (
                      <Button
                        key={day.value}
                        type={selectedDays.includes(day.value) ? 'primary' : 'default'}
                        onClick={() => {
                          if (selectedDays.includes(day.value)) {
                            setSelectedDays(selectedDays.filter(d => d !== day.value));
                          } else {
                            setSelectedDays([...selectedDays, day.value]);
                          }
                        }}
                      >
                        {day.label}
                      </Button>
                    ))}
                  </Space>
                </div>
              </div>
              <div>
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text>Chọn giờ</Typography.Text>
                  <Tooltip title="Chọn các khung giờ mà món ăn này được phép bán">
                    <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                  </Tooltip>
                </div>
                <div>
                  <Space wrap>
                    {Array.from({ length: 24 }, (_, i) => i).map(hour => (
                      <Button
                        key={hour}
                        type={selectedHours.includes(hour.toString()) ? 'primary' : 'default'}
                        onClick={() => {
                          if (selectedHours.includes(hour.toString())) {
                            setSelectedHours(selectedHours.filter(h => h !== hour.toString()));
                          } else {
                            setSelectedHours([...selectedHours, hour.toString()]);
                          }
                        }}
                      >
                        {`${hour}h`}
                      </Button>
                    ))}
                  </Space>
                </div>
              </div>
            </div>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loadingDish}
                className="admin-button"
              >
                {editingDish ? 'Cập nhật' : 'Thêm mới'}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <Modal
        title="Cấu hình giá theo nguồn"
        open={sourcePriceModalVisible}
        onCancel={() => setSourcePriceModalVisible(false)}
        onOk={handleAddSourcePrice}
        okText="Xác nhận"
        cancelText="Hủy"
      >
        <Form layout="vertical">
          <Form.Item
            label="Nguồn đơn"
            required
            rules={[{ required: true, message: 'Vui lòng chọn nguồn đơn!' }]}
          >
            <Select
              value={newSourcePrice.source}
              onChange={(value) => setNewSourcePrice({ ...newSourcePrice, source: value })}
              placeholder="Chọn nguồn đơn"
            >
              <Option value="ZALO">ZALO</Option>
              <Option value="FACEBOOK">FACEBOOK</Option>
              <Option value="CRM">CRM</Option>
              <Option value="MANG_VE">MANG VỀ</Option>
              <Option value="TAI_CHO">TẠI CHỖ</Option>
              <Option value="CALL_CENTER">CALL CENTER</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="Số tiền"
            required
            rules={[{ required: true, message: 'Vui lòng nhập số tiền!' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              value={newSourcePrice.price}
              onChange={(value) => setNewSourcePrice({ ...newSourcePrice, price: value || 0 })}
              formatter={formatInputNumberFormatter(6)}
              parser={(value) => parseFlexibleNumber(value) as any}
              min={0}
              addonAfter="đ"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Tour component */}
      <ConfigProvider locale={viVN}>
        <Tour
          open={isTourOpen}
          onClose={() => setIsTourOpen(false)}
          steps={tourSteps}
          indicatorsRender={(current, total) => (
            <span>{current + 1} / {total}</span>
          )}
          type="primary"
          arrow={true}
          placement="bottom"
          mask={{
            style: {
              boxShadow: 'inset 0 0 15px #333',
            },
          }}
        />
      </ConfigProvider>
    </AdminLayout>
  );
};

export default DishManagement; 